import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const categories = ['All Results', 'Auto Accidents', 'Workplace Accidents', 'Wrongful Death', 'Product Liability'];

const caseResults = [
  { amount: '$2,050,000', category: 'Wrongful Death', description: "Plaintiff's vehicle was struck by a work van that crossed the center line resulting in death." },
  { amount: '$1,850,000', category: 'Auto Accidents', description: 'Head-on collision caused severe injuries requiring multiple surgeries.' },
  { amount: '$1,000,000', category: 'Workplace Accidents', description: 'Worker was electrocuted and suffered a fall resulting in brain injuries and memory deficits.' },
  { amount: '$825,000', category: 'Wrongful Death', description: 'Vehicle struck head-on by a truck on a mountain road resulting in death.' },
  { amount: '$700,000', category: 'Product Liability', description: 'Faulty drug trial resulted in permanent knee injuries and knee replacement surgeries.' },
  { amount: '$500,000', category: 'Auto Accidents', description: 'Severe injuries following a rear-end collision.' },
  { amount: '$450,000', category: 'Workplace Accidents', description: 'Plaintiff sustained serious injuries in a car accident while working overseas.' },
  { amount: '$445,000', category: 'Auto Accidents', description: 'Multiple injuries sustained in a serious car wreck.' },
  { amount: '$402,240', category: 'Workplace Accidents', description: 'Car wreck while on the job resulting in death.' },
  { amount: '$390,000', category: 'Auto Accidents', description: 'Serious crash requiring surgery.' },
  { amount: '$245,000', category: 'Workplace Accidents', description: 'Plaintiff assaulted on the job resulting in injuries.' },
  { amount: '$245,000', category: 'Workplace Accidents', description: 'Severe arm injury requiring several surgeries.' },
  { amount: '$215,000', category: 'Workplace Accidents', description: 'Lower back injuries requiring surgery.' },
];

const CaseResults = () => {
  const [activeFilter, setActiveFilter] = useState('All Results');

  const filtered = activeFilter === 'All Results'
    ? caseResults
    : caseResults.filter(r => r.category === activeFilter);

  return (
    <div data-testid="case-results-page">
      {/* Header */}
      <section className="py-10 md:py-24 bg-slate-50">
        <div className="mx-auto px-4 md:px-8" style={{ maxWidth: '1120px' }}>
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-3xl sm:text-5xl md:text-6xl font-heading font-bold text-slate-900 mb-4 md:mb-6 tracking-tight">
              Case Results
            </h1>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed">
              Every case we take is personal. Behind every number is a client who was dealing with mounting medical bills, 
              lost wages, physical pain, and uncertainty about the future. Our job is to take that burden off their shoulders 
              and fight for the outcome they deserve.
            </p>
          </div>
        </div>
      </section>

      {/* Million Dollar Advocates Forum */}
      <section className="py-10 md:py-20 bg-slate-50" data-testid="mdaf-section">
        <div className="mx-auto px-4 md:px-8" style={{ maxWidth: '1120px' }}>
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
            {/* Logo */}
            <div className="flex-shrink-0">
              <img
                src="https://customer-assets.emergentagent.com/job_46114de2-d079-4505-8e4e-c293a92431a3/artifacts/cd9y10nf_DAS_-_Million_Dollar_Advocates_Forum_LOGO.png"
                alt="Million Dollar Advocates Forum"
                className="w-auto"
                style={{ maxHeight: '120px', maxWidth: '320px' }}
              />
            </div>

            {/* Description */}
            <div>
              <h2 className="text-2xl md:text-[30px] font-heading font-semibold text-slate-900 mb-3 md:mb-4 tracking-tight leading-tight">
                Million Dollar Advocates Forum
              </h2>
              <p className="text-[15px] md:text-[16px] text-slate-600 leading-relaxed mb-4">
                The Million Dollar Advocates Forum is one of the most prestigious groups of trial lawyers in the United States. 
                Membership is limited to attorneys who have won million-dollar and multi-million-dollar verdicts and settlements. 
                Fewer than 1% of U.S. lawyers are members, making it one of the most selective benchmarks of legal achievement 
                in the country.
              </p>
              <p className="text-[15px] md:text-[16px] text-slate-700 leading-relaxed font-medium">
                At Rizzi Law Group, three of our attorneys — Mike Rizzi, Ashton Gordon, and Taylor Gordon — have been admitted 
                to the Million Dollar Advocates Forum. This recognition reflects 
                our firm's ability to achieve significant results for our clients in serious injury and wrongful death cases.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Results Grid */}
      <section className="py-10 md:py-24 bg-white">
        <div className="mx-auto px-4 md:px-8" style={{ maxWidth: '1120px' }}>
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-[34px] font-heading font-semibold text-slate-900 mb-3 tracking-tight">
              Representative Results
            </h2>
            <p className="text-[15px] md:text-base text-slate-600 leading-relaxed max-w-2xl mx-auto">
              These results represent a sampling of cases handled by our firm. Every case is different, and outcomes 
              depend on the specific facts and circumstances involved.
            </p>
          </div>

          {/* Filter Pills */}
          <div className="flex flex-wrap gap-2 md:gap-3 justify-center mb-8 md:mb-12" data-testid="category-filters">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveFilter(cat)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  activeFilter === cat
                    ? 'bg-primary text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-testid="results-grid">
            {filtered.map((result, i) => (
              <div
                key={i}
                className="bg-slate-50 border border-slate-200 rounded-lg p-6 hover:shadow-md transition-shadow duration-200"
                data-testid={`case-result-${i}`}
              >
                <p className="text-2xl md:text-3xl font-heading font-bold text-slate-900 mb-3 tracking-tight">
                  {result.amount}
                </p>
                <span className="inline-block text-xs font-semibold text-primary bg-primary/10 px-3 py-1 rounded-full mb-3">
                  {result.category}
                </span>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {result.description}
                </p>
              </div>
            ))}
          </div>

          {/* Disclaimer */}
          <p className="text-sm text-slate-500 text-center mt-10 md:mt-12 leading-relaxed max-w-2xl mx-auto italic" data-testid="results-disclaimer">
            Past results do not guarantee a similar outcome. Each case must be evaluated on its own facts and circumstances.
          </p>
        </div>
      </section>

      {/* Client Impact Section */}
      <section className="py-10 md:py-20 bg-slate-50" data-testid="client-impact-section">
        <div className="mx-auto px-4 md:px-8" style={{ maxWidth: '1120px' }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16 items-start">
            {/* Left - What We Fight For */}
            <div>
              <h2 className="text-2xl md:text-[34px] font-heading font-semibold text-slate-900 mb-4 md:mb-6 tracking-tight leading-tight">
                It's Not Just About the Settlement
              </h2>
              <p className="text-[15px] md:text-[16px] text-slate-600 leading-relaxed mb-6">
                When someone is seriously injured, the financial pressure can be overwhelming. Medical bills pile up. 
                Time away from work creates stress at home. Ongoing treatment and rehabilitation costs add uncertainty 
                to an already difficult situation. We understand what's at stake, and we work to address the full picture.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <span className="text-primary font-bold leading-none">•</span>
                  <span className="text-[15px] md:text-[16px] text-slate-700">Covering current and future medical expenses</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-primary font-bold leading-none">•</span>
                  <span className="text-[15px] md:text-[16px] text-slate-700">Replacing lost income and earning capacity</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-primary font-bold leading-none">•</span>
                  <span className="text-[15px] md:text-[16px] text-slate-700">Securing resources for ongoing care and rehabilitation</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-primary font-bold leading-none">•</span>
                  <span className="text-[15px] md:text-[16px] text-slate-700">Relieving the stress of dealing with insurance companies</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-primary font-bold leading-none">•</span>
                  <span className="text-[15px] md:text-[16px] text-slate-700">Holding negligent parties accountable</span>
                </div>
              </div>
            </div>

            {/* Right - Finding All Coverage */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 md:p-8">
              <h3 className="text-xl md:text-2xl font-heading font-semibold text-slate-900 mb-4 tracking-tight">
                We Find Every Source of Coverage
              </h3>
              <p className="text-[15px] md:text-[16px] text-slate-600 leading-relaxed mb-5">
                One of the most important parts of building a strong case is identifying all available sources of 
                insurance coverage. Many injured clients don't realize there may be multiple policies that apply to their situation.
              </p>
              <p className="text-[15px] md:text-[16px] text-slate-600 leading-relaxed mb-5">
                We thoroughly investigate every potential source of recovery, including the at-fault party's liability 
                coverage, underinsured and uninsured motorist policies, umbrella policies, commercial insurance, and 
                workers' compensation benefits when applicable.
              </p>
              <p className="text-[15px] md:text-[16px] text-slate-700 leading-relaxed font-medium">
                Our goal is to make sure no stone is left unturned, so our clients receive the maximum compensation 
                available to them.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 md:py-24 bg-slate-50">
        <div className="mx-auto px-4 md:px-8 text-center" style={{ maxWidth: '1120px' }}>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-4 tracking-tight">
            Ready to Discuss Your Case?
          </h2>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto mb-6 md:mb-8">
            If you or a loved one has been injured due to someone else's negligence, our attorneys are ready 
            to help you understand your legal options.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center bg-primary text-white font-semibold hover:bg-primary/90 transition-all duration-300 px-6 md:px-8 py-3 rounded-lg text-[15px]"
            data-testid="case-results-cta"
          >
            Free Consultation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CaseResults;
